/**
 * Path rules, mirroring backend/apps/sandbox/paths.py: what the agent may write, and which sync class
 * a workspace file belongs to (spec 3.3).
 */
import type { SyncClass } from "./types";

export const WORKSPACE = "/workspace";
export const CONTEXT = "/context";

export const PATH_MAX_CHARS = 1024;
export const SEGMENT_MAX_BYTES = 255;
const NOT_KEPT_DIRS = new Set(["node_modules", "site-packages", "__pycache__", ".cache"]);
const NOT_KEPT_TOP = new Set(["tmp"]);
const CLASSES = new Set(["data", "uploads", "publish"]);
const FORBIDDEN_CHARS = /[\p{Cc}\p{Cf}\p{Cs}\\]/u;

/**
 * `path` against `cwd` as a normalised absolute path ("." and ".." folded, no trailing slash, NFC
 * like the server, so a name typed or uploaded decomposed is one file under one spelling).
 */
export function resolvePath(cwd: string, path: string): string {
  const joined = (path.startsWith("/") ? path : `${cwd}/${path}`).normalize("NFC");
  const out: string[] = [];
  for (const segment of joined.split("/")) {
    if (!segment || segment === ".") continue;
    if (segment === "..") out.pop();
    else out.push(segment);
  }
  return `/${out.join("/")}`;
}

export function dirname(path: string): string {
  const cut = path.lastIndexOf("/");
  return cut <= 0 ? "/" : path.slice(0, cut);
}

export function basename(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** `path` is `root` or inside it. */
export function isUnder(path: string, root: string): boolean {
  return path === root || path.startsWith(`${root}/`);
}

export type Zone = "workspace" | "context" | "system";

export function zoneOf(absPath: string): Zone {
  if (isUnder(absPath, WORKSPACE)) return "workspace";
  if (isUnder(absPath, CONTEXT)) return "context";
  return "system";
}

/** The workspace-relative form of an absolute path, or null outside /workspace (or the root itself). */
export function workspaceRelative(absPath: string): string | null {
  return absPath.startsWith(`${WORKSPACE}/`) ? absPath.slice(WORKSPACE.length + 1) : null;
}

/** Why the server would refuse this workspace path as a file name, or null when it is fine. */
export function nameProblem(absPath: string): string | null {
  const rel = workspaceRelative(absPath);
  if (rel === null) return "outside the workspace";
  if (rel.length > PATH_MAX_CHARS) return `paths can be up to ${PATH_MAX_CHARS} characters`;
  if (FORBIDDEN_CHARS.test(rel)) return "paths cannot hold control characters or backslashes";
  const encoder = new TextEncoder();
  if (rel.split("/").some((segment) => encoder.encode(segment).length > SEGMENT_MAX_BYTES)) {
    return `file and folder names can be up to ${SEGMENT_MAX_BYTES} bytes`;
  }
  return null;
}

/** Packages, caches and tmp: restored, never synced. */
export function isNotKept(absPath: string): boolean {
  const rel = workspaceRelative(absPath);
  if (rel === null) return true;
  const folded = rel.split("/").map((segment) => segment.toLowerCase());
  return (
    NOT_KEPT_TOP.has(folded[0]) ||
    folded.some((segment) => NOT_KEPT_DIRS.has(segment)) ||
    folded[folded.length - 1].endsWith(".pyc")
  );
}

/** The sync class of a workspace file; null outside /workspace. */
export function syncClassOf(absPath: string): SyncClass | null {
  const rel = workspaceRelative(absPath);
  if (rel === null) return null;
  if (isNotKept(absPath)) return "not_synced";
  const cut = rel.indexOf("/");
  const top = cut > 0 ? rel.slice(0, cut) : "";
  return CLASSES.has(top) ? (top as SyncClass) : "work";
}
